import { FC, useMemo } from 'react'

import dayjs from 'dayjs'
import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useBoardsList, useBoardsListPosts } from 'services/boards/boards.hooks'
import { useIntl } from 'react-intl'
import R4RIcon from 'components/R4RIcon'
import {
  WidgetCard,
  WidgetCardHeader,
  WidgetCardBody,
  WidgetBadge,
  WidgetOutlineButton
} from './WidgetCard'

const NEW_POST_DAYS = 7
const DESKTOP_POSTS_COUNT = 2

const WidgetAnnouncements: FC = () => {
  const { data = [] } = useBoardsList()
  const { boardsListPosts } = useBoardsListPosts(data.map(({ id }) => id))
  const { formatMessage } = useIntl()
  const navigate = useNavigate()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))

  const sortedPosts = useMemo(
    () =>
      [...boardsListPosts].sort(
        (a, b) => new Date(b.eventDate).getTime() - new Date(a.eventDate).getTime()
      ),
    [boardsListPosts]
  )

  const latestPost = sortedPosts[0] || null

  const newPostsCount = boardsListPosts.filter(({ eventDate }) =>
    dayjs(eventDate).isAfter(dayjs().subtract(NEW_POST_DAYS, 'day'))
  ).length

  const goToPost = () =>
    latestPost
      ? navigate(`/board/${latestPost.board.id}/${latestPost.id}`)
      : navigate('/board')

  if (isDesktop) {
    return (
      <WidgetCard sx={{ flex: 1, minWidth: 0 }}>
        <WidgetCardHeader
          icon='megaphone'
          title={formatMessage({ id: 'dashboard.announcements', defaultMessage: 'Komunikaty' })}
          badge={
            newPostsCount > 0 ? (
              <WidgetBadge background={theme.r4r.yellow} color={theme.r4r.ink}>
                {formatMessage(
                  { id: 'widgetAnnouncements.newCount', defaultMessage: '{count} nowe' },
                  { count: newPostsCount }
                )}
              </WidgetBadge>
            ) : undefined
          }
        />
        <WidgetCardBody>
          {sortedPosts.length === 0 && (
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: theme.r4r.ink }}>
              {formatMessage({
                id: 'WidgetAnnouncements.noEvents',
                defaultMessage: 'Brak wydarzeń'
              })}
            </Typography>
          )}
          {sortedPosts.slice(0, DESKTOP_POSTS_COUNT).map((post) => (
            <Box
              key={post.id}
              onClick={() => navigate(`/board/${post.board.id}/${post.id}`)}
              sx={{
                padding: '8px 0 9px',
                borderBottom: `1px solid ${theme.r4r.greyLight}`,
                cursor: 'pointer'
              }}>
              <Typography noWrap sx={{ fontSize: '14px', fontWeight: 600, color: theme.r4r.ink }}>
                {post.title}
              </Typography>
              <Typography sx={{ fontSize: '11px', color: theme.r4r.grey, mt: '2px' }}>
                {dayjs(post.eventDate).format('DD.MM.YYYY')}
              </Typography>
            </Box>
          ))}
          <Box sx={{ mt: 'auto', pt: '12px' }}>
            <WidgetOutlineButton onClick={() => navigate('/board')}>
              {formatMessage({ id: 'widgetAnnouncements.all', defaultMessage: 'Wszystkie' })}
            </WidgetOutlineButton>
          </Box>
        </WidgetCardBody>
      </WidgetCard>
    )
  }

  return (
    <WidgetCard sx={{ padding: '14px 18px 24px', gap: '10px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', color: theme.r4r.navy }}>
          <R4RIcon name='megaphone' size={16} />
          <Typography sx={{ fontSize: '15px', fontWeight: 700, color: theme.r4r.navy }}>
            {formatMessage({ id: 'dashboard.announcements', defaultMessage: 'Komunikaty' })}
          </Typography>
        </Box>
        {newPostsCount > 0 && (
          <WidgetBadge background={theme.r4r.yellow} color={theme.r4r.ink}>
            {formatMessage(
              { id: 'widgetAnnouncements.newCount', defaultMessage: '{count} nowe' },
              { count: newPostsCount }
            )}
          </WidgetBadge>
        )}
      </Box>
      <Box onClick={goToPost} sx={{ cursor: 'pointer' }}>
        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: theme.r4r.ink }}>
          {latestPost?.title ||
            formatMessage({
              id: 'WidgetAnnouncements.noEvents',
              defaultMessage: 'Brak wydarzeń'
            })}
        </Typography>
        {!!latestPost && (
          <Typography sx={{ fontSize: '11px', color: theme.r4r.grey }}>
            {dayjs(latestPost.eventDate).format('DD.MM.YYYY')}
          </Typography>
        )}
      </Box>
      <Button
        fullWidth
        variant='outlined'
        onClick={() => navigate('/board')}
        endIcon={<R4RIcon name='arrowForward' size={18} />}
        sx={{
          borderColor: theme.r4r.navy,
          color: theme.r4r.navy,
          fontSize: '13px',
          fontWeight: 500,
          minHeight: '28px',
          padding: '5px 13px',
          borderRadius: 0,
          '&:hover': { borderColor: theme.r4r.navy }
        }}>
        {formatMessage({ id: 'widgetAnnouncements.all', defaultMessage: 'Wszystkie' })}
      </Button>
    </WidgetCard>
  )
}

export default WidgetAnnouncements
